import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useLocation } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import CoiHistoryModal from './NominationShortlist/COI/COIHistoryModal';
import PageTitle from '../layouts/PageTitle';
import Markdown from './Forms/Elements/Markdown';
import DownloadFile from './Forms/Elements/DownloadFile';
import { arrayToString } from '../../utils/arrayToString';
import { arrayToNewLines } from '../../utils/arrayToNewLines';
import { formatDateDDMMYYYY } from '../../utils/formatDateDDMMYYYY';
import { stringUcFirst } from '../../utils/stringUcFirst';
import { handleDownload } from '../../utils/handleDownload';
import { getNominationRoundYear } from '../../utils/getNominationRoundYear';
import {
  ELECTION_TYPE,
  REFEREE_STATUS,
  strapiUrl,
} from '../../utils/constants';
import { getReportRoundYear } from '../../utils/getReportRoundYear';

import { CommonCollections } from '../../App';
import { getNominationFormData } from '../../utils/getNominationFormData';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import {
  downloadExistingPdf,
  getFilenameFromUrl,
} from '../../utils/downloadPdf.js';
import { devError } from '../../utils/devLog';
import {
  isFellowInactive,
  isNonAasRefereeInactive,
} from '../../utils/refereeHelpers';

const TextWithLineBreaks = ({ children, className = '' }) => {
  if (!children || typeof children !== 'string') {
    return <p className={className}>{children}</p>;
  }

  const lines = children.split('\n');

  if (lines.length > 1) {
    return (
      <p className={className}>
        {lines.map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  }

  return <p className={className}>{children}</p>;
};

TextWithLineBreaks.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

const NominationViewProposer = ({ hidePageTitle = false }) => {
  const { userDetails } = useLoggedInUserContextProvider();
  const {
    australianStatus,
    binariesTypes,
    coiTypes,
    electionTypes,
    formStepsArray,
    genderTypes,
    fellowsArray,
    nonAasRefereesArray,
    nominationHistoryArray,
    sectionalCommittees,
    sectionalCommitteeHistoryArray,
    stateTerritories,
    supporterTypes,
    titlesTypes,
    organisations,
    users,
  } = useStaticCollections();

  const {
    candidateStateArray,
    coiDeclarationArchiveArray,
    nominationArray,
    nominationStateArray,
    refereeReportArchive,
    refereeReports,
  } = useAdminCollections();

  const { nominationId, nominationHistoryLastConsideredDate } =
    useContext(CommonCollections);

  const location = useLocation();
  const currentPath = location.pathname;

  const [formViewValues, setFormViewValues] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [currentNominationState, setCurrentNominationState] = useState([]);
  const [currentCandidateState, setCurrentCandidateState] = useState([]);
  const [existingPdfUrl, setExistingPdfUrl] = useState(null);
  const [showCoiModal, setShowCoiModal] = useState(false);

  const [
    nominationSectionalCommitteesHistory,
    setNominationSectionalCommitteesHistory,
  ] = useState({});
  const formattedBinaryTypes = Object.fromEntries(
    Object.entries(binariesTypes).map(([key, value]) => [
      parseInt(key) - 1,
      value,
    ]),
  );

  const committeeYears = [
    { key: 'sectionalCommitteeYearOne', label: 'Primary committee year 1' },
    { key: 'sectionalCommitteeYearTwo', label: 'Primary committee year 2' },
    { key: 'sectionalCommitteeYearThree', label: 'Primary committee year 3' },
    { key: 'sectionalCommitteeYearFour', label: 'Primary committee year 4' },
  ];

  const isAdmin =
    userDetails.roles.includes('admin') ||
    userDetails.roles.includes('super-admin');

  const nominationCoiArchives = (coiDeclarationArchiveArray || []).filter(
    (declaration) => declaration?.attributes?.nominationId === nominationId,
  );

  const showCoiHistoryButton = isAdmin && nominationCoiArchives.length > 0;

  const renderCommitteeYear = (year) => {
    const yearValue = nominationSectionalCommitteesHistory[year.key];
    return yearValue ? (
      <div key={year.key}>
        <span className='fw-bold'>{year.label}</span>
        <p>{yearValue ?? ''}</p>
      </div>
    ) : null;
  };

  useEffect(() => {
    try {
      (async () => {
        if (nominationId) {
          const currentNomination = currentPath.includes('form-view-history')
            ? nominationHistoryArray.find(
                (item) =>
                  item?.attributes?.nominationId === nominationId &&
                  getNominationRoundYear(
                    new Date(item?.attributes?.lastConsideredDate),
                  ) === nominationHistoryLastConsideredDate,
              )
            : nominationArray.find((item) => item?.id === nominationId);

          let nominationFormValues = currentNomination?.attributes?.value
            ? JSON.parse(currentNomination.attributes.value)
            : null;

          if (!nominationFormValues) {
            nominationFormValues = await getNominationFormData(
              currentNomination,
              organisations,
            );
          }
          setFormViewValues(nominationFormValues);

          const nominationSectionalCommitteeHistory =
            sectionalCommitteeHistoryArray.find(
              (item) => item.attributes.nominationId === nominationId,
            );

          setNominationSectionalCommitteesHistory(
            nominationSectionalCommitteeHistory?.attributes ?? '',
          );

          const nominationState = currentPath.includes('form-view-history')
            ? nominationHistoryArray.find(
                (item) =>
                  item?.attributes?.nominationId === nominationId &&
                  getNominationRoundYear(
                    new Date(item?.attributes?.lastConsideredDate),
                  ) === nominationHistoryLastConsideredDate,
              )
            : nominationStateArray.find(
                (nominationStateItem) =>
                  nominationStateItem?.attributes?.nominationId ===
                  nominationId,
              );
          if (nominationState && currentNomination) {
            nominationState.attributes.nominationStatsTypeId =
              currentNomination.attributes?.nominationStatusTypeId;
          }

          setCurrentNominationState(nominationState);

          if (currentNomination && currentNomination?.attributes?.candidateId) {
            setCurrentCandidateState(
              candidateStateArray.find(
                (candidateStateItem) =>
                  candidateStateItem.attributes.candidateId ===
                  currentNomination.attributes.candidateId,
              ),
            );
          }

          if (currentNomination && currentNomination?.attributes?.pdfPath) {
            const pdfUrl = `${strapiUrl}${currentNomination?.attributes?.pdfPath}`;
            setExistingPdfUrl(pdfUrl);
          }

          setFormFields(formStepsArray);
        }
      })();
    } catch (error) {
      devError('Error in NominationViewProposer:', error);
    }
  }, [
    nominationId,
    organisations,
    nominationArray,
    formStepsArray,
    sectionalCommitteeHistoryArray,
    nominationStateArray,
    candidateStateArray,
    nominationHistoryArray,
    nominationHistoryLastConsideredDate,
    currentPath,
    strapiUrl,
  ]);

  const sortedFormFields = [...formFields].sort(
    (a, b) => a.attributes.formStep - b.attributes.formStep,
  );

  const pdfUrls = [];

  const roundYear = currentNominationState?.attributes?.lastConsideredDate
    ? getNominationRoundYear(
        new Date(currentNominationState.attributes.lastConsideredDate),
      )
    : null;

  const candidateName =
    `${formViewValues?.name} ${formViewValues?.lastName} ${roundYear}`
      .toLowerCase()
      .replace(/\s+/g, '-');

  const createRefereeReportObject = (report) => {
    const { nonAasRefereeId, refereeFellowId } = report.attributes;

    const matchingReferee = nonAasRefereeId
      ? nonAasRefereesArray.find((referee) => referee.id === nonAasRefereeId)
      : fellowsArray.find((fellow) => fellow.id === refereeFellowId);

    const {
      titleId,
      name = '',
      lastName = '',
    } = matchingReferee?.attributes || {};
    const title = titlesTypes[titleId] || '';
    const label = [title, name, lastName].filter(Boolean).join(' ');

    const updatedAt = report?.attributes?.updatedAt;
    const reportUpdatedYear = updatedAt
      ? getNominationRoundYear(new Date(updatedAt))
      : null;

    const reportRoundYear = getReportRoundYear(report.attributes);
    const reportData = report.attributes.report.data;

    return {
      refereeUserId: users[report.attributes.refereeUserId],
      path: `${strapiUrl}${reportData.attributes.url}`,
      independentAssessor: report.attributes.independentAssessor,
      refereeStatusId: report.attributes.refereeStatusId,
      label,
      reportRoundYear,
      nonAasRefereeId,
      refereeFellowId,
      reportUpdatedYear,
    };
  };

  const extractRefereeReports = (reportsArray) =>
    reportsArray.reduce((acc, report) => {
      if (
        report.attributes.nominationId === nominationId &&
        report.attributes.report !== null &&
        report.attributes.refereeArchived !== true &&
        (report.attributes.refereeStatusId === REFEREE_STATUS.SUBMITTED ||
          report.attributes.refereeStatusId === REFEREE_STATUS.USE_EXISTING)
      ) {
        const reportData = report.attributes.report.data;
        if (reportData) {
          acc.push(createRefereeReportObject(report));
        }
      }
      return acc;
    }, []);

  const pendingReferees = refereeReports
    .filter(
      (item) =>
        item?.attributes?.nominationId === nominationId &&
        item?.attributes?.refereeStatusId === REFEREE_STATUS.PENDING,
    )
    .map((item) => item.attributes.refereeUserId);

  const refereeReportHistory = refereeReportArchive
    .filter((item) => {
      if (item?.attributes?.nominationId !== nominationId) return false;
      if (!pendingReferees.includes(item.attributes.refereeUserId))
        return false;
      return (
        item?.attributes?.report?.data &&
        item?.attributes?.refereeStatusId !== REFEREE_STATUS.PENDING
      );
    })
    .sort(
      (a, b) =>
        new Date(b.attributes.updatedAt) - new Date(a.attributes.updatedAt),
    )
    .reduce((acc, report) => {
      const userId = report.attributes.refereeUserId;
      if (!acc.has(userId)) {
        acc.set(userId, report);
      }
      return acc;
    }, new Map())
    .values()
    .toArray()
    .map((report) => createRefereeReportObject(report));

  const urlsWithRefereeIds = (() => {
    const reportsFromCurrentReports = extractRefereeReports(refereeReports);
    return reportsFromCurrentReports.length > 0
      ? reportsFromCurrentReports
      : extractRefereeReports(refereeReportArchive);
  })();

  function extractReferees(data) {
    const referees = [];
    if (!data) return referees;
    const numbers = [
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
    ];

    numbers.forEach((num) => {
      const refereeKey = `referee${num}`;
      const fellowId = data[`${refereeKey}FellowId`];
      if (fellowId?.value) {
        referees.push({
          type: 'fellowId',
          value: fellowId.value,
        });
      }

      const nonAasId = data[`nonAasReferee${num}Id`];
      if (nonAasId?.value) {
        referees.push({
          type: 'nonAasRefereeId',
          value: nonAasId.value,
        });
      }
    });

    return referees;
  }

  const extractedReferees = extractReferees(formViewValues);

  function orderReferees(referees, orderingCriteria) {
    const ordered = orderingCriteria
      .map((criterion) =>
        referees.find(
          (referee) =>
            (criterion.type === 'fellowId' &&
              referee.refereeFellowId === criterion.value) ||
            (criterion.type === 'nonAasRefereeId' &&
              referee.nonAasRefereeId === criterion.value),
        ),
      )
      .filter(Boolean);

    const remaining = referees.filter((referee) => !ordered.includes(referee));

    return [...ordered, ...remaining];
  }

  const orderedUrlsWithRefereeIds = orderReferees(
    urlsWithRefereeIds,
    extractedReferees,
  );

  const refereeReportUrls =
    userDetails.roles.includes('admin') ||
    userDetails.roles.includes('super-admin') ||
    userDetails.roles.includes('secretary') ||
    userDetails.roles.includes('committee-member') ||
    userDetails.roles.includes('committee-chair') ||
    userDetails.roles.includes('council-observer')
      ? orderedUrlsWithRefereeIds
      : null;

  const isLoadingNomination =
    !!nominationId &&
    (nominationArray.length === 0 ||
      formFields.length === 0 ||
      !formViewValues ||
      Object.keys(formViewValues).length === 0);

  if (isLoadingNomination) {
    return (
      <>
        {!hidePageTitle && (
          <PageTitle activeMenu='View Nomination' motherMenu='Nominations' />
        )}
        <div className='card nomination-view-card'>
          <div className='card-body text-center py-5'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading nomination…</span>
            </div>
            <p className='mt-3 text-muted mb-0'>Loading nomination…</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!hidePageTitle && (
        <PageTitle activeMenu='View Nomination' motherMenu='Nominations' />
      )}

      <div className='card nomination-view-card'>
        <div className='card-header'>
          <h4 className='card-title'>Nomination View</h4>
          <div className='d-flex gap-2'>
            {showCoiHistoryButton && (
              <OverlayTrigger
                placement='bottom'
                overlay={<Tooltip>Candidate's COI History</Tooltip>}
              >
                <button
                  className='btn btn-info'
                  type='button'
                  onClick={() => setShowCoiModal(true)}
                  aria-label='COI History'
                >
                  <i className='bi bi-journal-text' />
                </button>
              </OverlayTrigger>
            )}
            <OverlayTrigger
              placement='bottom'
              overlay={<Tooltip>Download nomination in PDF</Tooltip>}
            >
              <button
                className='btn btn-success'
                type='button'
                onClick={() => {
                  if (
                    existingPdfUrl &&
                    currentPath.includes('form-view-history')
                  ) {
                    const filename = getFilenameFromUrl(existingPdfUrl);
                    downloadExistingPdf(
                      filename.replace('.pdf', ''),
                      existingPdfUrl,
                    );
                  } else {
                    const nominationStatusTypeId =
                      currentNominationState?.attributes
                        ?.nominationStatusTypeId;

                    handleDownload(
                      candidateName,
                      pdfUrls,
                      refereeReportUrls,
                      refereeReportHistory,
                      nominationStatusTypeId,
                      fellowsArray,
                      nonAasRefereesArray,
                    );
                  }
                }}
                aria-label='Download'
              >
                <i className='bi bi-download' />
              </button>
            </OverlayTrigger>
          </div>
        </div>
        {formViewValues &&
          formViewValues?.length !== 0 &&
          sortedFormFields.map((item, index) => {
            const formFieldsObj = item?.attributes?.formField || {};
            const filteredFormFields = Object.values(formFieldsObj).filter(
              (formFieldItem) =>
                formFieldItem.name === 'sectionalCommittee' ||
                formFieldItem.name === 'phdAwardedYear' ||
                formFieldItem.name === 'nationalityId' ||
                formFieldItem.name === 'australianStatusId' ||
                formFieldItem.name === 'genderId' ||
                formFieldItem.name === 'organisationId' ||
                formFieldItem.name === 'stateTerritoryId' ||
                (formFieldItem.name &&
                  formFieldItem.name.includes('referee')) ||
                formFieldItem.__component !==
                  'field-collection.field-collection',
            );

            const fieldDescriptionClassName = { marginTop: '70px' };
            const formTextStyle = {
              width: '55%',
            };

            const firstDivHeadingStyle =
              index === 0
                ? { width: '55%', paddingTop: '60px', paddingBottom: '60px' }
                : { width: '55%', paddingTop: '40px', paddingBottom: '20px' };

            if (formViewValues.organisationId.value === 0) {
              formViewValues.organisationId.label =
                formViewValues.otherOrganisation;
            }

            return (
              <React.Fragment key={index}>
                <div className='card-body text-left'>
                  <div
                    className='mx-auto text-center'
                    style={firstDivHeadingStyle}
                  >
                    <h3>{`${item.attributes.formStep}. ${item.attributes.heading}`}</h3>
                  </div>
                  {filteredFormFields.map((filteredFormFieldItem, indexTwo) => {
                    let currentType = [];
                    let currentNonAasReferee = {};

                    let filePath = '';
                    const originalPath =
                      formViewValues[`${filteredFormFieldItem.name}FilePath`];

                    if (typeof originalPath === 'string') {
                      const fileName = originalPath.split('/').pop()?.trim();

                      if (currentPath.includes('form-view-history')) {
                        const historyNomination = nominationHistoryArray.find(
                          (historyItem) =>
                            historyItem?.attributes?.nominationId ===
                            nominationId,
                        );

                        const folderName =
                          historyNomination?.attributes
                            ?.individualFilesFolderName;

                        if (folderName && fileName) {
                          formViewValues[
                            `${filteredFormFieldItem.name}FilePath`
                          ] =
                            `${strapiUrl}/nomination-history/files/${folderName}/${fileName}`;
                          filePath = `${strapiUrl}/nomination-history/files/${folderName}/${fileName}`;
                        } else {
                          filePath = originalPath;
                        }
                      } else {
                        filePath = originalPath;
                      }
                    }

                    if (filePath) {
                      pdfUrls.push({
                        name: filteredFormFieldItem.name,
                        path: filePath,
                      });
                    }

                    if (filteredFormFieldItem.name === 'electionTypeId') {
                      currentType = electionTypes;
                    }

                    if (filteredFormFieldItem.name === 'titleId') {
                      currentType = titlesTypes;
                    }

                    if (filteredFormFieldItem.name === 'genderId') {
                      currentType = genderTypes;
                    }

                    if (filteredFormFieldItem.name === 'stateTerritoryId') {
                      currentType = stateTerritories;
                    }

                    if (
                      filteredFormFieldItem.name === 'careerInterruption' ||
                      filteredFormFieldItem.name === 'meetsConduct'
                    ) {
                      currentType = formattedBinaryTypes;
                    }

                    if (filteredFormFieldItem.name === 'diverseBackground') {
                      currentType = { 0: 'No', 1: 'Yes' };
                    }

                    if (
                      filteredFormFieldItem.name === 'supporterOneTypeId' ||
                      filteredFormFieldItem.name === 'supporterTwoTypeId' ||
                      filteredFormFieldItem.name === 'supporterThreeTypeId' ||
                      filteredFormFieldItem.name === 'supporterFourTypeId'
                    ) {
                      currentType = supporterTypes;
                    }

                    if (filteredFormFieldItem.name === 'australianStatusId') {
                      currentType = australianStatus;
                    }

                    if (
                      formViewValues &&
                      filteredFormFieldItem?.name?.includes('referee') &&
                      formViewValues[
                        `nonAas${stringUcFirst(filteredFormFieldItem.name)}Id`
                      ]
                    ) {
                      const nonAasRefereeId =
                        formViewValues[
                          `nonAas${stringUcFirst(filteredFormFieldItem.name)}Id`
                        ].value;

                      currentNonAasReferee = nonAasRefereesArray.find(
                        (itemTest) => itemTest.id === nonAasRefereeId,
                      );
                    }

                    return (
                      <div
                        key={indexTwo}
                        className='col-md-12 mb-xxl-4 mx-auto'
                        style={formTextStyle}
                      >
                        {/* Round year */}
                        {filteredFormFieldItem.name === 'electionTypeId' &&
                          currentNominationState?.attributes?.roundYear && (
                            <>
                              <span className='fw-bold'>Round Year</span>
                              <p>
                                {currentNominationState?.attributes?.roundYear}
                              </p>
                            </>
                          )}

                        {/* Last considered */}
                        {filteredFormFieldItem.name === 'electionTypeId' &&
                          roundYear && (
                            <>
                              <span className='fw-bold'>Last Considered</span>
                              <p>{roundYear}</p>
                            </>
                          )}

                        {/* Relationships */}
                        {formViewValues[filteredFormFieldItem.name] !==
                          undefined &&
                          typeof formViewValues[filteredFormFieldItem.name] ===
                            'number' &&
                          !filteredFormFieldItem.name.includes('referee') && (
                            <div className='col-md-12'>
                              {filteredFormFields[indexTwo - 1] &&
                              filteredFormFields[indexTwo - 1].__component ===
                                'fieldset-description.fieldset-description' ? (
                                <div style={fieldDescriptionClassName}>
                                  <Markdown
                                    text={`#### ${
                                      filteredFormFields[indexTwo - 1]
                                        .fieldsetDescription
                                    }`}
                                    className='mb-3'
                                  />
                                </div>
                              ) : (
                                ''
                              )}
                              <span className='fw-bold'>
                                {filteredFormFieldItem.label}
                              </span>
                              <p>
                                {(() => {
                                  const key = filteredFormFieldItem.name;
                                  const rawValue = formViewValues[key];
                                  const resolved = currentType?.[rawValue];
                                  return resolved || rawValue;
                                })()}
                              </p>
                            </div>
                          )}

                        {/* Another gender */}
                        {filteredFormFieldItem.name === 'genderId' &&
                          formViewValues.otherGender && (
                            <>
                              <span className='fw-bold'>Other gender</span>
                              <p>{formViewValues.otherGender}</p>
                            </>
                          )}

                        {/* Sectional committee - label */}
                        {filteredFormFieldItem.name === 'sectionalCommittee' &&
                          (parseInt(formViewValues.electionTypeId) ===
                            ELECTION_TYPE.CORRESPONDING_MEMBER ||
                            parseInt(formViewValues.electionTypeId) ===
                              ELECTION_TYPE.SPECIAL_ELECTION) && (
                            <div>
                              <span className='fw-bold'>
                                Primary Sectional Committee
                              </span>
                              <p>
                                Corresponding Member and Special Election
                                Committee
                              </p>
                            </div>
                          )}

                        {/* Sectional committee */}
                        {filteredFormFieldItem.name === 'sectionalCommittee' &&
                          formViewValues.sectionalCommitteeId &&
                          formViewValues.sectionalCommitteeId !== 'none' && (
                            <div>
                              <span className='fw-bold'>
                                Primary Sectional Committee
                              </span>
                              <p>
                                {formViewValues.deprecatedSectionalCommitteeLabel &&
                                formViewValues.deprecatedSectionalCommitteeLabel !==
                                  ''
                                  ? formViewValues.deprecatedSectionalCommitteeLabel
                                  : sectionalCommittees[
                                      formViewValues.sectionalCommitteeId
                                    ]}
                              </p>
                            </div>
                          )}

                        {/* Sectional committee - discipline descriptor */}
                        {filteredFormFieldItem.name === 'sectionalCommittee' &&
                          formViewValues.disciplineDescriptor &&
                          formViewValues.disciplineDescriptor.length !== 0 && (
                            <div>
                              <span className='fw-bold'>
                                Discipline Descriptors
                              </span>
                              <p>{`${arrayToString(
                                formViewValues.disciplineDescriptor,
                              )}`}</p>
                            </div>
                          )}

                        {/* Sectional committee - interdisciplinary committee */}
                        {filteredFormFieldItem.name === 'sectionalCommittee' &&
                          formViewValues?.interdisciplinaryCommittee &&
                          formViewValues?.interdisciplinaryCommittee?.length !==
                            0 && (
                            <div>
                              <span className='fw-bold '>
                                Interdisciplinary Committees
                              </span>
                              <TextWithLineBreaks>
                                {`${arrayToNewLines(
                                  formViewValues?.interdisciplinaryCommittee,
                                )}`}
                              </TextWithLineBreaks>
                            </div>
                          )}

                        {/* Sectional committee - overlap sectional committee */}
                        {filteredFormFieldItem.name === 'sectionalCommittee' &&
                          formViewValues.overlapSectionalCommitteeId &&
                          formViewValues.overlapSectionalCommitteeId !==
                            'none' &&
                          formViewValues.overlapSectionalCommitteeId !== 0 &&
                          sectionalCommittees[
                            formViewValues.overlapSectionalCommitteeId
                          ] && (
                            <div>
                              <span className='fw-bold'>
                                Overlap Sectional Committee
                              </span>
                              <p>
                                {
                                  sectionalCommittees[
                                    formViewValues.overlapSectionalCommitteeId
                                  ]
                                }
                              </p>
                            </div>
                          )}

                        {/* Sectional committee  - overlap discipline descriptor */}
                        {filteredFormFieldItem.name === 'sectionalCommittee' &&
                          formViewValues?.overlapDisciplineDescriptor &&
                          formViewValues?.overlapDisciplineDescriptor
                            ?.length !== 0 && (
                            <div>
                              <span className='fw-bold'>
                                Overlap discipline descriptors
                              </span>
                              <p>{`${arrayToString(
                                formViewValues.overlapDisciplineDescriptor,
                              )}`}</p>
                            </div>
                          )}

                        {/* Sectional committee history */}
                        {filteredFormFieldItem.name === 'sectionalCommittee' &&
                          sectionalCommitteeHistoryArray.length > 0 &&
                          committeeYears.some(
                            (year) =>
                              nominationSectionalCommitteesHistory[year.key] &&
                              nominationSectionalCommitteesHistory[
                                year.key
                              ].trim() !== '',
                          ) && (
                            <div className='mt-xl-5'>
                              <h4 className='mb-3'>
                                Sectional Committee history
                              </h4>
                              {committeeYears.map((year) =>
                                renderCommitteeYear(year),
                              )}
                            </div>
                          )}

                        {/* Field set descriptions */}
                        {typeof formViewValues[filteredFormFieldItem.name] ===
                          'string' &&
                          formViewValues[filteredFormFieldItem.name] !==
                            'none' &&
                          Number.isNaN(
                            parseInt(
                              formViewValues[filteredFormFieldItem.name],
                            ),
                          ) &&
                          formViewValues[filteredFormFieldItem.name] !== '' &&
                          !filteredFormFieldItem.name.includes('referee') && (
                            <div className='col-md-12'>
                              {filteredFormFields[indexTwo - 1] &&
                              filteredFormFields[indexTwo - 1].__component ===
                                'fieldset-description.fieldset-description' ? (
                                <div style={fieldDescriptionClassName}>
                                  <Markdown
                                    text={`#### ${
                                      filteredFormFields[indexTwo - 1]
                                        .fieldsetDescription
                                    }`}
                                    className='mb-3'
                                  />
                                </div>
                              ) : (
                                ''
                              )}
                              {filteredFormFieldItem.name !== 'citation' && (
                                <span className='fw-bold'>
                                  {filteredFormFieldItem.label}
                                </span>
                              )}

                              <TextWithLineBreaks>
                                {formViewValues[filteredFormFieldItem.name]}
                              </TextWithLineBreaks>
                            </div>
                          )}

                        {/* Existing string values in all steps */}
                        {typeof formViewValues[filteredFormFieldItem.name] ===
                          'string' &&
                          !Number.isNaN(
                            parseInt(
                              formViewValues[filteredFormFieldItem.name],
                            ),
                          ) &&
                          formViewValues[filteredFormFieldItem.name] !== '' &&
                          !filteredFormFieldItem.name.includes('referee') &&
                          filteredFormFieldItem.name !== 'dob' && (
                            <div
                              className={`col-md-12 ${
                                filteredFormFieldItem.name === 'meetsConduct'
                                  ? 'mt-7'
                                  : ''
                              }`}
                            >
                              {filteredFormFields[indexTwo - 1] &&
                              filteredFormFields[indexTwo - 1].__component ===
                                'fieldset-description.fieldset-description' ? (
                                <div style={fieldDescriptionClassName}>
                                  <Markdown
                                    text={`#### ${
                                      filteredFormFields[indexTwo - 1]
                                        .fieldsetDescription
                                    }`}
                                    className='mb-3'
                                  />
                                </div>
                              ) : (
                                ''
                              )}
                              <span className='fw-bold'>
                                {filteredFormFieldItem.label}
                              </span>
                              <TextWithLineBreaks>
                                {(currentType &&
                                currentType[
                                  formViewValues[filteredFormFieldItem.name]
                                ]
                                  ? currentType[
                                      formViewValues[filteredFormFieldItem.name]
                                    ]
                                  : formViewValues[
                                      filteredFormFieldItem.name
                                    ]) +
                                  (filteredFormFieldItem.name ===
                                    'australianStatusId' &&
                                  formViewValues.arrivalDate
                                    ? `: ${formatDateDDMMYYYY(formViewValues.arrivalDate)}`
                                    : '')}
                              </TextWithLineBreaks>
                            </div>
                          )}

                        {/* Number of years considered */}
                        {filteredFormFieldItem.name === 'electionTypeId' &&
                          (() => {
                            const consideredYears =
                              currentCandidateState?.attributes
                                ?.consideredYears ||
                              formViewValues?.consideredYears ||
                              (currentPath.includes('form-view-history') &&
                                nominationHistoryArray?.find(
                                  (h) =>
                                    h?.attributes?.nominationId ===
                                    nominationId,
                                )?.attributes?.consideredYears);

                            return consideredYears &&
                              consideredYears !== '0' ? (
                              <>
                                <span className='fw-bold'>
                                  Number of years previously considered
                                </span>
                                <p>{consideredYears}</p>
                              </>
                            ) : null;
                          })()}

                        {/* 1. Candidate's Details - NationalityId */}
                        {filteredFormFieldItem.name &&
                          filteredFormFieldItem.name === 'australianStatusId' &&
                          formViewValues.nationalityId && (
                            <div
                              className={`col-md-12 ${
                                filteredFormFieldItem.name === 'meetsConduct'
                                  ? 'mt-7'
                                  : ''
                              }`}
                            >
                              <span className='fw-bold'>
                                {formViewValues.australianStatusId
                                  ? filteredFormFieldItem.label
                                  : 'Nationality'}
                              </span>
                              <p>
                                {formViewValues.nationalityId
                                  ? arrayToString(formViewValues.nationalityId)
                                  : ''}
                              </p>
                            </div>
                          )}

                        {/* Strings - dates DD/MM/YYYY */}
                        {typeof formViewValues[filteredFormFieldItem.name] ===
                          'string' &&
                          !Number.isNaN(
                            parseInt(
                              formViewValues[filteredFormFieldItem.name],
                            ),
                          ) &&
                          formViewValues[filteredFormFieldItem.name] !== '' &&
                          !filteredFormFieldItem.name.includes('referee') &&
                          filteredFormFieldItem.name === 'dob' && (
                            <div
                              className={`col-md-12 ${
                                filteredFormFieldItem.name === 'meetsConduct'
                                  ? 'mt-7'
                                  : ''
                              }`}
                            >
                              {filteredFormFields[indexTwo - 1] &&
                              filteredFormFields[indexTwo - 1].__component ===
                                'fieldset-description.fieldset-description' ? (
                                <div style={fieldDescriptionClassName}>
                                  <Markdown
                                    text={`#### ${
                                      filteredFormFields[indexTwo - 1]
                                        .fieldsetDescription
                                    }`}
                                    className='mb-3'
                                  />
                                </div>
                              ) : (
                                ''
                              )}
                              <span className='fw-bold'>
                                {filteredFormFieldItem.label}
                              </span>
                              <p>
                                {`${
                                  currentType &&
                                  currentType[
                                    formViewValues[filteredFormFieldItem.name]
                                  ]
                                    ? currentType[
                                        formViewValues[
                                          filteredFormFieldItem.name
                                        ]
                                      ]
                                    : formatDateDDMMYYYY(
                                        formViewValues[
                                          filteredFormFieldItem.name
                                        ],
                                      )
                                }`}
                              </p>
                            </div>
                          )}

                        {/* 1. Candidate's Details - Boolean */}
                        {typeof formViewValues[filteredFormFieldItem.name] ===
                          'boolean' &&
                          formViewValues[filteredFormFieldItem.name] !== '' && (
                            <div className='col-md-12'>
                              {filteredFormFields[indexTwo - 1] &&
                              filteredFormFields[indexTwo - 1].__component ===
                                'fieldset-description.fieldset-description' ? (
                                <div style={fieldDescriptionClassName}>
                                  <Markdown
                                    text={`#### ${
                                      filteredFormFields[indexTwo - 1]
                                        .fieldsetDescription
                                    }`}
                                    className='mb-3'
                                  />
                                </div>
                              ) : (
                                ''
                              )}
                              <span className='fw-bold'>
                                {filteredFormFieldItem.label}
                              </span>
                              <p>
                                {`${
                                  formViewValues[filteredFormFieldItem.name] ===
                                  true
                                    ? 'Yes'
                                    : 'No'
                                }`}
                              </p>
                            </div>
                          )}

                        {/* Files */}
                        {formViewValues[
                          `${filteredFormFieldItem.name}FileName`
                        ] &&
                          formViewValues[
                            `${filteredFormFieldItem.name}FileName`
                          ] !== '' && (
                            <div className='col-md-12'>
                              {filteredFormFields[indexTwo - 1] &&
                              filteredFormFields[indexTwo - 1].__component ===
                                'fieldset-description.fieldset-description' ? (
                                <div style={fieldDescriptionClassName}>
                                  <Markdown
                                    text={`#### ${
                                      filteredFormFields[indexTwo - 1]
                                        .fieldsetDescription
                                    }`}
                                    className='mb-3'
                                  />
                                </div>
                              ) : (
                                ''
                              )}
                              <span className='fw-bold'>
                                {filteredFormFieldItem.label}
                              </span>
                              <div className='mt-2'>
                                <DownloadFile
                                  text={
                                    formViewValues[
                                      `${filteredFormFieldItem.name}FileName`
                                    ]
                                  }
                                  url={
                                    formViewValues[
                                      `${filteredFormFieldItem.name}FilePath`
                                    ]
                                  }
                                  name={`${formViewValues.name}${formViewValues.lastName}_${filteredFormFieldItem.name}`}
                                  roundYear={roundYear}
                                />
                              </div>
                            </div>
                          )}

                        {/* Proposer header and details */}
                        {filteredFormFieldItem.name === 'proposerFellowId' &&
                          formViewValues[filteredFormFieldItem.name] &&
                          typeof formViewValues[filteredFormFieldItem.name] ===
                            'object' &&
                          formViewValues[filteredFormFieldItem.name].label && (
                            <div className='col-md-12 mt-xl-5'>
                              <h4 className='mb-3'>
                                <strong>Proposer</strong>
                              </h4>
                              <div style={{ marginBottom: '0.5rem' }}>
                                <span className='fw-bold'>Name</span>
                                <TextWithLineBreaks>
                                  {
                                    formViewValues[filteredFormFieldItem.name]
                                      .label
                                  }
                                </TextWithLineBreaks>
                              </div>
                            </div>
                          )}

                        {/* Seconder header and details */}
                        {filteredFormFieldItem.name === 'seconderFellowId' &&
                          formViewValues[filteredFormFieldItem.name] &&
                          typeof formViewValues[filteredFormFieldItem.name] ===
                            'object' &&
                          formViewValues[filteredFormFieldItem.name].label && (
                            <div className='col-md-12 mt-xl-5'>
                              <h4 className='mb-3'>
                                <strong>Seconder</strong>
                              </h4>
                              <div style={{ marginBottom: '0.5rem' }}>
                                <span className='fw-bold'>Name</span>
                                <TextWithLineBreaks>
                                  {
                                    formViewValues[filteredFormFieldItem.name]
                                      .label
                                  }
                                </TextWithLineBreaks>
                              </div>
                            </div>
                          )}

                        {filteredFormFieldItem.name &&
                        filteredFormFieldItem.name !== 'proposerFellowId' &&
                        filteredFormFieldItem.name !== 'seconderFellowId' &&
                        formViewValues[filteredFormFieldItem.name] &&
                        typeof formViewValues[filteredFormFieldItem.name] ===
                          'object' &&
                        formViewValues[filteredFormFieldItem.name].label ? (
                          <div
                            className={`col-md-12 ${/(proposer|seconder)/i.test(filteredFormFieldItem.label) ? 'mt-xl-5' : ''}`}
                          >
                            {filteredFormFields[indexTwo - 1] &&
                            filteredFormFields[indexTwo - 1].__component ===
                              'fieldset-description.fieldset-description' ? (
                              <div style={fieldDescriptionClassName}>
                                <Markdown
                                  text={`#### ${
                                    filteredFormFields[indexTwo - 1]
                                      .fieldsetDescription
                                  }`}
                                  className='mb-3'
                                />
                              </div>
                            ) : (
                              ''
                            )}
                            <span className='fw-bold'>
                              {filteredFormFieldItem.label}
                            </span>
                            <TextWithLineBreaks>
                              {formViewValues[filteredFormFieldItem.name].label}
                            </TextWithLineBreaks>
                          </div>
                        ) : (
                          ''
                        )}

                        {/* 10. Referees - referee name */}
                        {formViewValues[
                          `${filteredFormFieldItem.name}FellowId`
                        ] &&
                        Object.values(
                          formViewValues[
                            `${filteredFormFieldItem.name}FellowId`
                          ],
                        ).length > 0 &&
                        formattedBinaryTypes[
                          formViewValues[`${filteredFormFieldItem.name}`]
                        ] ? (
                          <>
                            <h4
                              className={
                                filteredFormFieldItem.name === 'refereeOne'
                                  ? ''
                                  : 'mt-xl-5'
                              }
                              style={{ marginBottom: '0.5rem' }}
                            >
                              Referee{' '}
                              {filteredFormFieldItem.label.match(/\d+/)[0]} (AAS
                              Fellow)
                            </h4>
                            <div
                              className='col-md-12'
                              style={{ marginBottom: '2rem' }}
                            >
                              {filteredFormFields[indexTwo - 1] &&
                              filteredFormFields[indexTwo - 1].__component ===
                                'fieldset-description.fieldset-description' ? (
                                <div style={fieldDescriptionClassName}>
                                  <Markdown
                                    text={`#### ${
                                      filteredFormFields[indexTwo - 1]
                                        .fieldsetDescription
                                    }`}
                                    className='mb-3'
                                  />
                                </div>
                              ) : (
                                ''
                              )}
                              <p>
                                {
                                  formViewValues[
                                    `${filteredFormFieldItem.name}FellowId`
                                  ].label
                                }
                                {isFellowInactive(
                                  formViewValues[
                                    `${filteredFormFieldItem.name}FellowId`
                                  ].value,
                                  fellowsArray,
                                ) && (
                                  <span className='text-danger fw-bold'>
                                    {' '}
                                    (Deceased)
                                  </span>
                                )}
                              </p>
                            </div>
                          </>
                        ) : (
                          typeof filteredFormFieldItem.name === 'string' &&
                          formViewValues[
                            `nonAas${stringUcFirst(filteredFormFieldItem.name)}Id`
                          ] && (
                            <>
                              <h4
                                className={
                                  filteredFormFieldItem.name === 'refereeOne'
                                    ? ''
                                    : 'mt-xl-5'
                                }
                                style={{ marginBottom: '0.5rem' }}
                              >
                                Referee{' '}
                                {filteredFormFieldItem.label.match(/\d+/)[0]}{' '}
                                (Non-AAS Referee)
                              </h4>
                              <div
                                className='col-md-12'
                                style={{ marginBottom: '2rem' }}
                              >
                                {filteredFormFields[indexTwo - 1] !==
                                  undefined &&
                                filteredFormFields[indexTwo - 1].__component ===
                                  'fieldset-description.fieldset-description' ? (
                                  <div style={fieldDescriptionClassName}>
                                    <Markdown
                                      text={`#### ${
                                        filteredFormFields[indexTwo - 1]
                                          .fieldsetDescription
                                      }`}
                                      className='mb-3'
                                    />
                                  </div>
                                ) : (
                                  ''
                                )}
                                <p style={{ marginBottom: '1rem' }}>
                                  {formViewValues[
                                    `nonAas${stringUcFirst(filteredFormFieldItem.name)}Id`
                                  ]?.label ?? ''}
                                  {isNonAasRefereeInactive(
                                    formViewValues[
                                      `nonAas${stringUcFirst(filteredFormFieldItem.name)}Id`
                                    ].value,
                                    nonAasRefereesArray,
                                  ) && (
                                    <span className='text-danger fw-bold'>
                                      {' '}
                                      (Inactive)
                                    </span>
                                  )}
                                </p>
                                {currentNonAasReferee?.attributes
                                  ?.organisation && (
                                  <div style={{ marginBottom: '0.5rem' }}>
                                    <span className='fw-bold'>
                                      Organisation
                                    </span>
                                    <TextWithLineBreaks>
                                      {
                                        currentNonAasReferee?.attributes
                                          ?.organisation
                                      }
                                    </TextWithLineBreaks>
                                  </div>
                                )}
                                {currentNonAasReferee?.attributes
                                  ?.memberships && (
                                  <div style={{ marginBottom: '0.5rem' }}>
                                    <span className='fw-bold'>Memberships</span>
                                    <TextWithLineBreaks>
                                      {
                                        currentNonAasReferee?.attributes
                                          ?.memberships
                                      }
                                    </TextWithLineBreaks>
                                  </div>
                                )}
                              </div>
                            </>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              </React.Fragment>
            );
          })}
      </div>

      {/* COI History Modal */}
      <CoiHistoryModal
        show={showCoiModal}
        onHide={() => setShowCoiModal(false)}
        coiArchives={nominationCoiArchives}
        users={users}
        coiTypes={coiTypes}
      />
    </>
  );
};

NominationViewProposer.propTypes = {
  hidePageTitle: PropTypes.bool,
};

export default NominationViewProposer;
